<template>
  <section class="py-20 px-6 bg-soft-white overflow-hidden" ref="sectionRef">
    <div class="container mx-auto max-w-6xl">
      <!-- Section Header -->
      <div
        class="text-center mb-16"
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{ opacity: 1, y: 0 }"
        :duration="800"
      >
        <h2 class="text-4xl md:text-5xl font-poppins font-semibold text-dark-gray mb-6">
          Trusted by <span class="text-light-purple">Leading Institutions</span>
        </h2>
        <div class="w-24 h-1 bg-light-purple mx-auto rounded-full mb-8"></div>
        <p class="text-lg text-dark-gray/80 max-w-3xl mx-auto leading-relaxed">
          We're proud to partner with industry leaders who trust us with their healthcare needs.
        </p>
      </div>

      <!-- Stats Section -->
      <div
        class="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16"
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{ opacity: 1, y: 0 }"
        :duration="800"
        :delay="200"
      >
        <div
          v-for="(stat, index) in stats"
          :key="index"
          class="text-center card-neumorphic cursor-pointer"
          v-motion
          :initial="{ opacity: 0, scale: 0.8 }"
          :visible-once="{ opacity: 1, scale: 1 }"
          :duration="500"
          :delay="400 + index * 100"
          @mouseenter="onStatHover"
          @mouseleave="onStatLeave"
        >
          <div class="text-3xl md:text-4xl font-bold text-light-purple mb-2">
            {{ stat.number }}
          </div>
          <div class="text-dark-gray/70 font-medium">
            {{ stat.label }}
          </div>
        </div>
      </div>

      <!-- Logo Carousel -->
      <div
        class="relative"
        v-motion
        :initial="{ opacity: 0 }"
        :visible-once="{ opacity: 1 }"
        :duration="800"
        :delay="600"
      >
        <!-- Gradient Overlays -->
        <div class="absolute left-0 top-0 w-20 h-full bg-gradient-to-r from-soft-white to-transparent z-10"></div>
        <div class="absolute right-0 top-0 w-20 h-full bg-gradient-to-l from-soft-white to-transparent z-10"></div>
        
        <!-- Scrolling Container -->
        <div class="overflow-hidden">
          <div class="flex space-x-12 logo-scroll">
            <div
              v-for="(logo, index) in [...clientLogos, ...clientLogos]"
              :key="index"
              class="flex-shrink-0 mx-4"
            >
              <div class="w-32 h-20 flex items-center justify-center p-4 bg-white rounded-lg shadow-soft hover:shadow-md transition-all duration-300">
                <img
                  :src="logo.src"
                  :alt="logo.alt"
                  class="max-h-12 max-w-24 object-contain opacity-70 hover:opacity-100 transition-all duration-300 filter grayscale hover:grayscale-0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Testimonial Section -->
      <div
        class="text-center mt-16"
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{ opacity: 1, y: 0 }"
        :duration="800"
        :delay="800"
      >
        <div class="card-neumorphic max-w-4xl mx-auto">
          <blockquote class="text-xl md:text-2xl text-dark-gray/80 italic mb-6 leading-relaxed">
            "Homvisit has transformed how we approach employee health screenings. Their professional team and comprehensive services have made healthcare more accessible for our workforce."
          </blockquote>
          <div class="flex items-center justify-center space-x-4">
            <div class="w-12 h-12 rounded-full bg-gradient-to-br from-light-purple to-purple-600 flex items-center justify-center text-white font-bold">
              HR
            </div>
            <div class="text-left">
              <div class="font-semibold text-dark-gray">Healthcare Partner</div>
              <div class="text-dark-gray/60 text-sm">Leading Corporate Client</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'ClientsSection',
  setup() {
    const sectionRef = ref(null)

    const stats = [
      { number: '500+', label: 'Happy Clients' },
      { number: '10K+', label: 'Tests Completed' },
      { number: '50+', label: 'Corporate Partners' },
      { number: '99%', label: 'Satisfaction Rate' }
    ]

    const onStatHover = (e) => {
      e.currentTarget.style.transform = 'scale(1.05)'
    }

    const onStatLeave = (e) => {
      e.currentTarget.style.transform = 'scale(1)'
    }

    const clientLogos = [
      { src: require('@/img/TATA-Consultancy-Services-logo-01.png'), alt: 'TATA Consultancy Services' },
      { src: require('@/img/accenture.png'), alt: 'Accenture' },
      { src: require('@/img/ey.jpg'), alt: 'EY' },
      { src: require('@/img/Bajaj_Finserv_Logo.svg.png'), alt: 'Bajaj Finserv' },
      { src: require('@/img/manipal health.jpg'), alt: 'Manipal Health' },
      { src: require('@/img/redcliffelabs_logo.jpg'), alt: 'Redcliffe Labs' },
      { src: require('@/img/sbi.jpg'), alt: 'State Bank of India' },
      { src: require('@/img/Guidehouse.jpg'), alt: 'Guidehouse' },
      { src: require('@/img/fingent_technologies_logo.jpg'), alt: 'Fingent Technologies' },
      { src: require('@/img/ortho fx.png'), alt: 'Ortho FX' },
      { src: require('@/img/uis global.png'), alt: 'UIS Global' },
      { src: require('@/img/terrific minds.png'), alt: 'Terrific Minds' },
      { src: require('@/img/Good methods.jpg'), alt: 'Good Methods' }
    ]

    return {
      sectionRef,
      stats,
      clientLogos,
      onStatHover,
      onStatLeave
    }
  }
}
</script>

<style scoped>
.logo-scroll {
  animation: scroll 30s linear infinite;
}

@keyframes scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.logo-scroll:hover {
  animation-play-state: paused;
}
</style>
